// ============================================================================
// FACILITATOR AVAILABILITY — Tier 1 facilitators set home studios, a weekly
// pattern and single-date exceptions. Feeds the free-facilitator counts on the
// Room Calendar. Any Tier 1 / system admin can edit any Tier 1 facilitator.
// ============================================================================

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { UserCheck, ChevronLeft, ChevronRight, Loader, AlertCircle, X, Check, CalendarRange } from 'lucide-react';
import {
  listFacilitators,
  getUserAvailability,
  setWeeklyAvailability,
  setAvailabilityDays,
  setHomeStudios,
  FacilitatorSummary,
  UserAvailability,
  AvailabilitySlot,
  AvailabilityException,
  Weekday
} from '../services/studio-bookings';
import { listAllRoomsWithStatus, RoomWithAvailability } from '../services/rooms';
import { DEFAULT_ROOM_COLOR } from '../constants/roomColors';
import { useAuth } from '../contexts/AuthContext';

const WEEKDAYS: { key: Weekday; label: string }[] = [
  { key: 'mon', label: 'Monday' },
  { key: 'tue', label: 'Tuesday' },
  { key: 'wed', label: 'Wednesday' },
  { key: 'thu', label: 'Thursday' },
  { key: 'fri', label: 'Friday' }
];
const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

type RuleChoice = AvailabilitySlot | null;
const RULE_OPTIONS: [RuleChoice, string][] = [[null, 'Available'], ['morning', 'AM off'], ['afternoon', 'PM off'], ['full_day', 'Off']];

type DayChoice = AvailabilitySlot | 'none' | null;
const DAY_OPTIONS: [DayChoice, string][] = [
  ['full_day', 'Unavailable all day'],
  ['morning', 'Unavailable morning'],
  ['afternoon', 'Unavailable afternoon'],
  ['none', 'Working (override weekly pattern)'],
  [null, 'Clear — back to weekly pattern']
];

const SLOT_SHORT: Record<AvailabilitySlot, string> = { full_day: 'Off', morning: 'AM off', afternoon: 'PM off' };

const pad = (n: number) => String(n).padStart(2, '0');
const ymd = (y: number, m: number, d: number) => `${y}-${pad(m)}-${pad(d)}`;

// Weekdays of the month, laid out Mon–Fri with leading blanks
function weekdayGrid(year: number, month: number): (string | null)[] {
  const days = new Date(year, month, 0).getDate();
  const cells: (string | null)[] = [];
  for (let d = 1; d <= days; d++) {
    const dow = new Date(year, month - 1, d).getDay();
    if (dow === 0 || dow === 6) continue;
    if (!cells.length) for (let i = 1; i < dow; i++) cells.push(null);
    cells.push(ymd(year, month, d));
  }
  return cells;
}

function weekdayKey(date: string): Weekday {
  return WEEKDAYS[new Date(`${date}T00:00:00`).getDay() - 1].key;
}

// Next state when a day is clicked: full → AM → PM → (working, if the weekly pattern
// says off) → back to the weekly pattern
function nextDayChoice(current: AvailabilityException | undefined, ruleSlot: AvailabilitySlot | undefined): DayChoice {
  const order: DayChoice[] = ruleSlot ? ['none', 'full_day', 'morning', 'afternoon', null] : ['full_day', 'morning', 'afternoon', null];
  const idx = current ? order.indexOf(current.unavailable) : -1;
  return order[(idx + 1) % order.length];
}

export default function FacilitatorAvailability() {
  const { user } = useAuth();
  const [facilitators, setFacilitators] = useState<FacilitatorSummary[]>([]);
  const [rooms, setRooms] = useState<RoomWithAvailability[]>([]);
  const [selectedId, setSelectedId] = useState<string>('');
  const [data, setData] = useState<UserAvailability | null>(null);
  const [rule, setRule] = useState<UserAvailability['weekdays']>({});
  const [ruleDirty, setRuleDirty] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);

  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);

  const [rangeFrom, setRangeFrom] = useState('');
  const [rangeTo, setRangeTo] = useState('');
  const [rangeChoice, setRangeChoice] = useState<DayChoice>('full_day');
  const [rangeReason, setRangeReason] = useState('');

  const tier1 = useMemo(
    () => facilitators.filter(f => f.tier === 'tier1' && !f.isOffboarded).sort((a, b) => a.displayName.localeCompare(b.displayName)),
    [facilitators]
  );

  useEffect(() => {
    Promise.all([listFacilitators(), listAllRoomsWithStatus()])
      .then(([f, r]) => {
        setFacilitators(f);
        setRooms(r);
        const mine = f.find(x => x.userId === user?.userId && x.tier === 'tier1');
        setSelectedId(mine?.userId || f.find(x => x.tier === 'tier1' && !x.isOffboarded)?.userId || '');
      })
      .catch(() => setError('Failed to load facilitators'))
      .finally(() => setLoading(false));
  }, [user?.userId]);

  const load = useCallback(async () => {
    if (!selectedId) return;
    try {
      const d = await getUserAvailability(selectedId, year, month);
      setData(d);
      setRule(d.weekdays);
      setRuleDirty(false);
    } catch {
      setError('Failed to load availability');
    }
  }, [selectedId, year, month]);

  useEffect(() => { load(); }, [load]);

  function flash(message: string) {
    setSaved(message);
    setTimeout(() => setSaved(null), 2500);
  }

  async function run(action: () => Promise<void>, message: string) {
    setSaving(true);
    setError(null);
    try {
      await action();
      await load();
      flash(message);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  function toggleStudio(roomId: string) {
    if (!data) return;
    const next = data.homeRoomIds.includes(roomId)
      ? data.homeRoomIds.filter(id => id !== roomId)
      : [...data.homeRoomIds, roomId];
    run(async () => {
      const homeRoomIds = await setHomeStudios(selectedId, next);
      setFacilitators(prev => prev.map(f => (f.userId === selectedId ? { ...f, homeRoomIds } : f)));
    }, 'Home studios saved');
  }

  function clickDay(date: string) {
    const current = data?.exceptions.find(e => e.date === date);
    const choice = nextDayChoice(current, data?.weekdays[weekdayKey(date)]);
    run(() => setAvailabilityDays(selectedId, { startDate: date, unavailable: choice, reason: current?.reason }), 'Saved');
  }

  function applyRange() {
    if (!rangeFrom) return;
    run(async () => {
      await setAvailabilityDays(selectedId, {
        startDate: rangeFrom,
        endDate: rangeTo || rangeFrom,
        unavailable: rangeChoice,
        reason: rangeReason.trim() || undefined
      });
      setRangeFrom(''); setRangeTo(''); setRangeReason('');
    }, 'Dates saved');
  }

  function prevMonth() {
    if (month === 1) { setYear(y => y - 1); setMonth(12); } else setMonth(m => m - 1);
  }
  function nextMonth() {
    if (month === 12) { setYear(y => y + 1); setMonth(1); } else setMonth(m => m + 1);
  }

  const exceptionByDate = useMemo(
    () => new Map((data?.exceptions || []).map(e => [e.date, e])),
    [data]
  );
  const cells = useMemo(() => weekdayGrid(year, month), [year, month]);
  const selected = tier1.find(f => f.userId === selectedId);
  const isSelf = selectedId === user?.userId;

  if (loading) {
    return (
      <div className="flex-1 overflow-y-auto p-8 font-display flex items-center justify-center">
        <Loader className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto p-8 font-display">
      <div className="w-full max-w-[1400px] mx-auto flex flex-col gap-6">

        <header className="flex items-center justify-between border-b border-slate-200 pb-6 gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary">
              <UserCheck size={22} />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-navy">Facilitator Availability</h1>
              <p className="text-slate-500 text-sm font-medium">
                Tier 1 facilitators count as available unless marked off here. Counts show on the Room Calendar per home studio.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={selectedId}
              onChange={e => setSelectedId(e.target.value)}
              className="h-10 border border-[#e2e8f0] rounded-xl px-3 text-sm text-navy bg-white focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
            >
              {tier1.map(f => (
                <option key={f.userId} value={f.userId}>
                  {f.displayName}{f.userId === user?.userId ? ' (you)' : ''}{!f.homeRoomIds?.length ? ' — no home studio' : ''}
                </option>
              ))}
            </select>
            <Link
              to="/studio/calendar"
              className="flex items-center justify-center gap-2 rounded-xl h-10 px-4 border border-slate-200 bg-white text-slate-600 text-sm font-bold hover:bg-slate-50 shadow-sm transition-colors"
            >
              <CalendarRange size={16} /> Room Calendar
            </Link>
          </div>
        </header>

        {error && (
          <div className="p-4 rounded-xl bg-red-50 border border-red-200 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
            <p className="text-sm text-red-700 flex-1">{error}</p>
            <button onClick={() => setError(null)}><X className="w-4 h-4 text-red-400" /></button>
          </div>
        )}
        {saved && (
          <div className="p-3 rounded-xl bg-green-50 border border-green-200 flex items-center gap-2 text-sm text-green-700">
            <Check className="w-4 h-4" /> {saved}
          </div>
        )}

        {!selected ? (
          <div className="rounded-xl bg-white p-12 border border-slate-200 text-center">
            <p className="text-slate-500 text-sm">No Tier 1 facilitators found.</p>
          </div>
        ) : !data ? (
          <div className="flex justify-center py-20"><Loader className="w-6 h-6 animate-spin text-slate-400" /></div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-6 items-start">

            <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
              {/* Home studios */}
              <div className="p-5 border-b border-[#e2e8f0]">
                <p className="text-[11px] font-bold text-primary tracking-widest uppercase mb-1">Home studios</p>
                <p className="text-xs text-slate-400 mb-3">
                  {isSelf ? 'You count' : `${selected.displayName} counts`} towards the free-facilitator figure for these studios only.
                </p>
                <div className="flex flex-col gap-2">
                  {rooms.map(r => {
                    const on = data.homeRoomIds.includes(r.roomId);
                    return (
                      <button
                        key={r.roomId}
                        type="button"
                        disabled={saving}
                        onClick={() => toggleStudio(r.roomId)}
                        className={`flex items-center justify-between p-3 rounded-xl border text-left transition-all disabled:opacity-60 ${
                          on ? 'border-primary bg-primary/5' : 'border-[#e2e8f0] hover:border-primary/40 hover:bg-slate-50'
                        }`}
                      >
                        <span className="flex items-center gap-2.5 text-sm font-semibold text-navy">
                          <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: r.color || DEFAULT_ROOM_COLOR }} />
                          {r.name}
                        </span>
                        {on && <Check className="w-4 h-4 text-primary" />}
                      </button>
                    );
                  })}
                </div>
                {!data.homeRoomIds.length && (
                  <p className="mt-3 text-xs font-semibold text-amber-600">No home studio set — not counted anywhere on the calendar.</p>
                )}
              </div>

              {/* Weekly pattern */}
              <div className="p-5">
                <p className="text-[11px] font-bold text-primary tracking-widest uppercase mb-1">Weekly pattern</p>
                <p className="text-xs text-slate-400 mb-3">Regular days off. Single dates on the right override this.</p>
                <div className="flex flex-col gap-2">
                  {WEEKDAYS.map(({ key, label }) => (
                    <div key={key} className="flex items-center gap-3">
                      <span className="w-20 text-sm font-medium text-slate-600">{label}</span>
                      <div className="flex flex-1 rounded-xl border border-[#e2e8f0] overflow-hidden">
                        {RULE_OPTIONS.map(([value, text]) => (
                          <button
                            key={text}
                            type="button"
                            onClick={() => { setRule(prev => ({ ...prev, [key]: value ?? undefined })); setRuleDirty(true); }}
                            className={`flex-1 py-1.5 text-xs font-semibold transition-all ${
                              (rule[key] ?? null) === value
                                ? (value ? 'bg-slate-700 text-white' : 'bg-primary text-white')
                                : 'bg-white text-slate-500 hover:bg-slate-50'
                            }`}
                          >
                            {text}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  disabled={!ruleDirty || saving}
                  onClick={() => run(() => setWeeklyAvailability(selectedId, rule), 'Weekly pattern saved')}
                  className="mt-4 w-full flex items-center justify-center gap-2 rounded-xl h-10 px-6 bg-primary hover:bg-primary-dark transition-colors text-white text-sm font-bold shadow-sm disabled:opacity-50"
                >
                  Save weekly pattern
                </button>
              </div>
            </div>

            <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
              {/* Range form */}
              <div className="p-5 border-b border-[#e2e8f0]">
                <p className="text-[11px] font-bold text-primary tracking-widest uppercase mb-3">Set dates</p>
                <div className="grid grid-cols-2 md:grid-cols-[1fr_1fr_1.4fr] gap-3">
                  <input
                    type="date"
                    value={rangeFrom}
                    onChange={e => setRangeFrom(e.target.value)}
                    className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                  />
                  <input
                    type="date"
                    value={rangeTo}
                    min={rangeFrom || undefined}
                    onChange={e => setRangeTo(e.target.value)}
                    placeholder="To (optional)"
                    className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                  />
                  <select
                    value={rangeChoice ?? 'clear'}
                    onChange={e => setRangeChoice(e.target.value === 'clear' ? null : e.target.value as DayChoice)}
                    className="col-span-2 md:col-span-1 w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-white"
                  >
                    {DAY_OPTIONS.map(([value, text]) => <option key={text} value={value ?? 'clear'}>{text}</option>)}
                  </select>
                </div>
                <div className="flex gap-3 mt-3">
                  <input
                    type="text"
                    value={rangeReason}
                    onChange={e => setRangeReason(e.target.value)}
                    placeholder="Reason (optional, e.g. annual leave)"
                    className="flex-1 border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                  />
                  <button
                    type="button"
                    disabled={!rangeFrom || saving}
                    onClick={applyRange}
                    className="flex items-center justify-center gap-2 rounded-xl h-10 px-6 bg-primary hover:bg-primary-dark transition-colors text-white text-sm font-bold shadow-sm disabled:opacity-50"
                  >
                    Apply
                  </button>
                </div>
                <p className="mt-2 text-xs text-slate-400">Weekends in the range are skipped. Leave "to" empty for a single day.</p>
              </div>

              {/* Month grid */}
              <div className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-[11px] font-bold text-primary tracking-widest uppercase">Month view</p>
                  <div className="flex items-center border border-[#e2e8f0] rounded-xl overflow-hidden bg-white h-9">
                    <button onClick={prevMonth} className="px-2 h-full text-slate-400 hover:text-navy hover:bg-slate-50"><ChevronLeft className="w-4 h-4" /></button>
                    <span className="px-3 text-sm font-semibold text-navy min-w-[130px] text-center">{MONTH_NAMES[month - 1]} {year}</span>
                    <button onClick={nextMonth} className="px-2 h-full text-slate-400 hover:text-navy hover:bg-slate-50"><ChevronRight className="w-4 h-4" /></button>
                  </div>
                </div>
                <div className="grid grid-cols-5 gap-1.5">
                  {DAY_LABELS.map(d => (
                    <div key={d} className="text-center text-xs font-bold uppercase tracking-wider text-slate-400 py-1">{d}</div>
                  ))}
                  {cells.map((date, i) => {
                    if (!date) return <div key={`blank-${i}`} />;
                    const exception = exceptionByDate.get(date);
                    const ruleSlot = data.weekdays[weekdayKey(date)];
                    let label = 'Available';
                    let cls = 'border-[#e2e8f0] bg-white text-slate-500 hover:border-primary/40';
                    if (exception?.unavailable === 'none') {
                      label = 'Working';
                      cls = 'border-primary/40 bg-primary/5 text-primary';
                    } else if (exception) {
                      label = SLOT_SHORT[exception.unavailable as AvailabilitySlot];
                      cls = 'border-red-200 bg-red-50 text-red-700';
                    } else if (ruleSlot) {
                      label = `${SLOT_SHORT[ruleSlot]} · weekly`;
                      cls = 'border-slate-200 bg-slate-100 text-slate-500';
                    }
                    return (
                      <button
                        key={date}
                        type="button"
                        disabled={saving}
                        onClick={() => clickDay(date)}
                        title={exception?.reason || 'Click to cycle: off → AM off → PM off → clear'}
                        className={`h-16 rounded-xl border p-1.5 text-left transition-colors disabled:opacity-60 ${cls}`}
                      >
                        <span className="block text-xs font-bold text-navy">{Number(date.slice(8))}</span>
                        <span className="block text-[10px] font-semibold truncate">{label}</span>
                        {exception?.reason && <span className="block text-[10px] truncate opacity-75">{exception.reason}</span>}
                      </button>
                    );
                  })}
                </div>
                <p className="mt-3 text-xs text-slate-400">Click a day to cycle its status. Grey days come from the weekly pattern.</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
